import Collection from 'core/model/Collection';
import { ALARM_NULL_END_DATE } from '@kentik/ui-shared/alerting/constants';
import { flaggedAsnRoleText } from '@kentik/ui-shared/synthetics/bgp';
import {
  alarmStateRenderer,
  destinationRenderer,
  durationRenderer,
  groupedSourceSummaryRenderer,
  severityRenderer,
  testTypeRenderer
} from 'app/views/synthetics/utils/alarmsTableRenderers';
import { alarmElapsed } from 'app/views/synthetics/utils/alarmElapsed';
import { legacyFilterValues } from 'app/views/synthetics/utils/bgpLegacy';
import { isAlarmActive, getFirstTriggerTime } from '@kentik/ui-shared/synthetics/utils';

class IncidentLogCollection extends Collection {
  get defaultSortState() {
    return { field: 'end_time', direction: 'asc' };
  }

  get filterFieldWhitelist() {
    return new Set(['details', 'end_time', 'key', 'severity', 'start_time', 'state']);
  }

  getSortValue = (field, model) => {
    const { $syn } = this.store;
    const value = model.get(field);

    if (field === 'source') {
      const agentId = model.get('key.value.agentId');

      if (agentId) {
        // per-agent listing, just the label from the single agent
        return $syn.agents.get(agentId)?.label || '-';
      }

      // some kind of grouping, use the standard group labels
      return groupedSourceSummaryRenderer({ $syn, groupData: model.get('details.groupData'), raw: true });
    }

    if (field === 'details.target') {
      return destinationRenderer({ raw: true })({ value: model.get(field), model });
    }

    if (field === 'details') {
      const { metrics } = value || {};

      return (
        // join all the metric labels as a single string
        (metrics || []).map(({ metric }) => metric).join('') || '-'
      );
    }

    if (field === 'duration') {
      return durationRenderer({ model });
    }

    if (field === 'elapsed') {
      return (
        alarmElapsed({
          startTime: model.get('start_time'),
          endTime: model.get('end_time'),
          state: model.get('state')
        })?.elapsedMs ?? -1
      );
    }

    /*
      The BGP summary column reads as prose, which sorts alphabetically into nonsense. Rank it by
      the vantage points behind each alarm instead, which is what the sentence leads with.
    */
    if (field === 'details.bgp.summary') {
      return model.get('details.bgp.affectedVpCount') ?? -1;
    }

    // a reachability alarm has no flagged ASN, so those sort together rather than on undefined
    if (field === 'details.bgp.topFlaggedAsn') {
      return model.get('details.bgp.topFlaggedAsn') ?? -1;
    }

    if (field === 'end_time' && isAlarmActive(model.get('state'))) {
      return 'N/A';
    }

    if (field === 'event_start_time') {
      return getFirstTriggerTime(value) || 'N/A';
    }

    if (field === 'test') {
      const { testId } = model.get('key.value') || {};

      if (testId) {
        const test = $syn.getTest(testId);

        if (test) {
          return test.get('display_name');
        }
      }

      return '-';
    }

    if (field === 'testType') {
      return testTypeRenderer({ model });
    }

    return value;
  };

  getFilterValues(model) {
    const { $syn } = this.store;
    const agentId = model.get('key.value.agentId');
    const groupData = model.get('details.groupData');
    const metrics = (model.get('details.metrics') || [])
      .map((metric) => `${metric.metric} ${metric.severity}`)
      .join(' ');
    const rawEndTime = model.get('end_time');
    const endTime = !rawEndTime || rawEndTime === ALARM_NULL_END_DATE ? 'n/a now' : rawEndTime;
    const bgp = model.get('details.bgp') || {};
    // both the finding and the ASNs it blames, so searching an ASN finds the alarms naming it
    const bgpFlaggedAsns = (bgp.flaggedAsns || []).map((row) => `AS${row.asn} ${flaggedAsnRoleText(row)}`).join(' ');

    return [
      model.id,
      metrics,
      severityRenderer({ raw: true })({ value: model.get('severity') }), // returns the raw severity label
      alarmStateRenderer({ raw: true })({ value: model.get('state') }), // returns the raw state label
      model.get('details.target'),
      model.get('start_time'),
      endTime,
      $syn.agents.get(agentId)?.filterValue || 'unknown or removed',
      groupedSourceSummaryRenderer({ $syn, groupData, raw: true }),
      bgp.display,
      bgp.prefix,
      bgp.summary,
      bgpFlaggedAsns,
      ...legacyFilterValues(bgp)
    ];
  }
}

export default IncidentLogCollection;
